import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AdminLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const denied = params.get("denied") === "1";
  const err = params.get("err");
  const [showJwt, setShowJwt] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Already an admin? go straight to dashboard
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const signInWithGoogle = () => {
    // Derive redirect from browser origin — required by Emergent Auth
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const jwtSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      if (u.role !== "admin") {
        toast.error("This account is not authorised for admin.");
        return;
      }
      navigate("/admin", { replace: true });
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A2540] px-5 py-16 text-white" data-testid="admin-login-page">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#2F80ED]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-[#34D8A0]/20 blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="mb-10 inline-flex items-center gap-2.5 font-display text-lg font-extrabold tracking-tight" data-testid="admin-login-logo">
          <img src="/logo-icon.png" alt="Physio Essential" className="h-9 w-9 object-contain" />
          Physio<span className="font-medium text-[#34D8A0]"> essential</span>
        </Link>

        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-overline text-white/70">
          <ShieldCheck className="h-3 w-3 text-[#34D8A0]" /> Private Admin Access
        </div>
        <h1 className="mt-3 font-display text-5xl font-black leading-[1.05] tracking-tight sm:text-6xl">
          Sign in to run<br />the store.
        </h1>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
          Only whitelisted Google accounts can reach the admin console. Everyone else lands right back here.
        </p>

        {denied && (
          <div className="mt-8 flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200" data-testid="admin-denied">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-semibold">Not authorised</div>
              <div className="mt-1 text-red-200/80">This Google account is not on the admin whitelist. Sign in with the authorised account.</div>
            </div>
          </div>
        )}
        {err && (
          <div className="mt-8 rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
            Sign-in failed. Please try again.
          </div>
        )}

        <button
          onClick={signInWithGoogle}
          className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-4 font-semibold text-black transition-transform hover:scale-[1.02]"
          data-testid="google-signin-btn"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          Sign in with Google
        </button>

        <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-overline text-white/30">
          <div className="h-px flex-1 bg-white/10" />
          Or
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {!showJwt ? (
          <button
            onClick={() => setShowJwt(true)}
            className="mt-6 flex w-full items-center justify-center gap-2 font-mono text-xs uppercase tracking-overline text-white/50 transition-colors hover:text-white"
            data-testid="jwt-toggle"
          >
            <KeyRound className="h-3.5 w-3.5" /> Use backup email &amp; password
          </button>
        ) : (
          <form onSubmit={jwtSubmit} className="mt-6 space-y-3" data-testid="jwt-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              required
              autoComplete="username"
              className="w-full rounded-md border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#34D8A0] focus:outline-none"
              data-testid="jwt-email"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              autoComplete="current-password"
              className="w-full rounded-md border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#34D8A0] focus:outline-none"
              data-testid="jwt-password"
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#2F80ED] px-6 py-3 font-mono text-xs uppercase tracking-overline text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
              data-testid="jwt-submit"
            >
              {busy ? "Signing in…" : "Sign in with password"}
            </button>
            <button
              type="button"
              onClick={() => setShowJwt(false)}
              className="block w-full text-center font-mono text-[10px] uppercase tracking-overline text-white/40 hover:text-white/70"
            >
              ← Back to Google sign-in
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
